// --------------------- Import React ---------------------
import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

// --------------------- Import Hooks ---------------------
import { useCotaUsuario } from "../hooks/useCotaUsuario";
import { useCotas } from "../hooks/useCotas";
import { useBuscarCota } from "../hooks/useBuscarCota";
import { useApartamento } from "../hooks/useApto";
import { useEmpreendimentoUsuario } from "../hooks/useEmpreendimentoUsuario";
import { useAtualizarDadosConfiguracaoPropriedade } from "../hooks/useAtualizarDadosConfiguracaoPropriedade";

// --------------------- Import Styles ---------------------
import { ContainerModal, TitleModal, TextModal, formStyle } from "../styles";

// --------------------- Import Antd-Design ---------------------
import { Modal, Form, Input, Select, InputNumber, Skeleton } from "antd";
// --------------------- Import Interfaces ---------------------
import { IModal } from "../interfaces/Modal";
import { Cota } from "../interfaces/Cota";

// --------------------- Import Constants ---------------------
import { diasDaSemana } from "../constant/diasDaSemanaOptions";
import { CotaUsuario } from "../interfaces/CotaUsuario";
import { layout } from "../constant/layout";
import { useWarning } from "../../../uteis/mensagens/useWarning";
import { useSuccess } from "../../../uteis/mensagens/useSuccess";
import { useError } from "../../../uteis/mensagens/useError";

export default function ModalConfiguracaoPropriedade({
  isModalOpen,
  handleFechar,
  cotaUsuarioId,
}: IModal) {
  const [formAtualizarCota] = Form.useForm();
  const [formAtualizarPrioridade] = Form.useForm();

  // Estado que recebe os valores para atualização da Cota
  const [atualizarCota, setAtualizarCota] = useState<Cota>({});

  const [atualizarPrioridade, setAtualizarPrioridade] = useState<CotaUsuario>(
    {}
  );
  // Exibe a Mensagem de  Erro
  const { showError, contextHolder: errorContextHolder } = useError();

  // Exibe a Mensagem de  Sucesso
  const { showSuccess, contextHolder: successContextHolder } = useSuccess();

  // Exibe a Mensagem de  Sucesso
  const { showWarning, contextHolder: warningContextHolder } = useWarning();

  // Estado para controlar se o erro já foi exibido
  const [errorDisplayed, setErrorDisplayed] = useState(false);

  // Estado para controlar se o sucesso já foi exibido
  const [successDisplayed, setSuccessDisplayed] = useState(false);

  // Estado para controlar se o warning já foi exibido
  const [warningDisplayed, setWarningDisplayed] = useState(false);

  // Hook de navegação do React Router
  const navigate = useNavigate();

  // Use para buscar as info de Cota Usuário
  const { isLoading: isLoadingCotaUsuario, cotaUsuario } = useCotaUsuario(
    cotaUsuarioId,
    isModalOpen
  );

  // Use para buscar a cota
  const { isLoading: isLoadingCota, cota } = useBuscarCota(
    cotaUsuario?.cotaId,
    isModalOpen
  );
  // Use para buscar o apartamento
  const { isLoading: isLoadingApartamento, apartamento } = useApartamento(
    cota?.aptoId,
    isModalOpen
  );

  // Use para buscar o empreendimento do usuario cadastrou
  const { isLoading: isLoadingEmpreendimento, empreendimentoUsuario } =
    useEmpreendimentoUsuario(apartamento?.empreendId, isModalOpen);

  // Use para buscar as cotas
  const { cotas } = useCotas(
    empreendimentoUsuario?.cronograma,
    apartamento?.id,
    apartamento?.origem,
    isModalOpen
  );

  // Função que atualiza os dados
  const {
    atualizarDadosConfiguracaoPropriedade,
    error,
    success,
    warning,
    isLoading,
    resetError,
    resetWarning,
  } = useAtualizarDadosConfiguracaoPropriedade();

  useEffect(() => {
    if (isModalOpen) {
      formAtualizarCota.setFieldsValue({
        cotaId: cotaUsuario?.cotaId,
        numeroCota: cota?.id,
        diaDaSemanaInicio: cota?.diaDaSemanaInicio,
        qtdSemanas: cota?.qtdSemanas,
      });
      formAtualizarPrioridade.setFieldsValue({
        prioridade: cotaUsuario?.prioridade,
      });
    }
  }, [
    isModalOpen,
    cotaUsuario,
    cota,
    formAtualizarCota,
    formAtualizarPrioridade,
  ]); // Dependências: isModalOpen,  endereco, usuario,usuarioRepresentante,enderecoRepresentante

  // useEffect para lidar com o sucesso
  useEffect(() => {
    if (isModalOpen) {
      if (success && !successDisplayed) {
        // Verifica se o sucesso já foi exibido
        showSuccess(success);
        setSuccessDisplayed(true);
        setTimeout(() => {
          navigate(0); // Use navigate(0) para simular um refresh completo
        }, 3000);
      }
      if (error && !errorDisplayed) {
        // Verifica se o erro já foi exibido
        showError(error);
        setErrorDisplayed(true); // Marca que o erro foi exibido

        // Limpa estado de error após 3 segundos
        setTimeout(() => {
          setErrorDisplayed(false);
          resetError();
        }, 3000);
      }
      if (warning && !warningDisplayed) {
        // Verifica se o warning já foi exibido
        showWarning(warning);
        setWarningDisplayed(true); // Marca que o warning foi exibido

        // Limpa estado de warning após 3 segundos
        setTimeout(() => {
          setWarningDisplayed(false);
          resetWarning();
        }, 5000);
      }
    }

    // Limpa o estado de sucesso quando o modal é fechado
    if (!isModalOpen) {
      setSuccessDisplayed(false);
      setErrorDisplayed(false);
      setWarningDisplayed(false);
    }
  }, [
    isModalOpen,
    error,
    success,
    warning,
    errorDisplayed,
    successDisplayed,
    warningDisplayed,
    showError,
    showSuccess,
    showWarning,
    resetError,
    resetWarning,
    navigate,
  ]);

  const cotaChange = (changedValues: Partial<any>, allValues: any) => {
    const cota = {
      qtdSemanas: allValues.qtdSemanas,
      diaDaSemanaInicio: allValues.diaDaSemanaInicio,
    };
    const cotaUsuario = {
      cotaId: allValues.numeroCota,
    };
    setAtualizarCota((prevState) => ({
      ...prevState,
      ...cota,
    }));
    setAtualizarPrioridade({
      ...cotaUsuario,
    });
  };

  const prioridadeChange = (changedValues: Partial<any>, allValues: any) => {
    setAtualizarPrioridade((prevState: any) => ({
      ...prevState,
      prioridade: allValues.prioridade,
    }));
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      cancelText="Cancelar"
      okText="Atualizar"
      onOk={() =>
        atualizarDadosConfiguracaoPropriedade(
          cotaUsuario?.cotaId,
          atualizarCota,
          cotaUsuarioId,
          atualizarPrioridade
        )
      }
      confirmLoading={isLoading}
      okButtonProps={{
        style: {
          backgroundColor: "#003644",
          borderColor: "#003644",
          color: "white",
        }, // Botão "Ativar" com fundo verde
      }}
      cancelButtonProps={{
        style: {
          backgroundColor: "#f44336",
          borderColor: "#f44336",
          color: "white",
        }, // Botão "Cancelar" com fundo vermelho
      }}
    >
      {isLoadingCotaUsuario &&
        isLoadingCota &&
        isLoadingApartamento &&
        isLoadingEmpreendimento && <Skeleton active />}
      {!isLoadingCotaUsuario &&
        cotaUsuario &&
        !isLoadingCota &&
        cota &&
        !isLoadingApartamento &&
        apartamento &&
        !isLoadingEmpreendimento &&
        empreendimentoUsuario && (
          <>
            <ContainerModal>
              <div key="error-message-holder">{errorContextHolder}</div>
              <div key="success-message-holder">{successContextHolder}</div>
              <div key="warning-message-holder"> {warningContextHolder}</div>
              <TitleModal>Configuração de Propriedadade</TitleModal>
              <Form
                {...layout}
                name="cota"
                form={formAtualizarCota}
                onValuesChange={(changedValues, allValues) =>
                  cotaChange(changedValues, allValues)
                }
                style={formStyle}
              >
                <TextModal>Atualizar Cota</TextModal>
                <Form.Item
                  name={["numeroCota"]}
                  label="Número da cota"
                  className="formItems"
                >
                  {empreendimentoUsuario?.origem === 0 ? (
                    <Select placeholder="Cota">
                      {cotas.map((cota) => (
                        <Select.Option key={cota.id} value={cota.id}>
                          {cota.numeroCota}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <Input placeholder="Cota" />
                  )}
                </Form.Item>
                <Form.Item
                  name={["qtdSemanas"]}
                  label="Semanas por ano"
                  className="formItems"
                >
                  <InputNumber
                    max={4}
                    placeholder="Quantas semanas tem direito por ano"
                  />
                </Form.Item>
                <Form.Item
                  name={["diaDaSemanaInicio"]}
                  label="Dia Da Semana"
                  className="formItems"
                >
                  <Select placeholder="Selecione o dia da semana">
                    {diasDaSemana.map((dias) => (
                      <Select.Option key={dias.id} value={dias.value}>
                        {dias.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Form>
              <Form
                {...layout}
                form={formAtualizarPrioridade}
                onValuesChange={(changedValues, allValues) =>
                  prioridadeChange(changedValues, allValues)
                }
                style={formStyle}
              >
                <TextModal>Atualizar Prioridade</TextModal>
                <Form.Item
                  name="prioridade"
                  label="Prioridade"
                  className="formItems"
                >
                  <Input placeholder="Prioridade" />
                </Form.Item>
              </Form>
            </ContainerModal>
          </>
        )}
    </Modal>
  );
}
